import { useState } from 'react';
import {
  checkCargoBarcode,
  checkDropBarcode,
  formatAddress,
  formatPhone,
  allowedServices,
  boxNeedsDims,
  paymentsTotal,
  searchCities,
  withCashPayment,
  type BoxDraft,
  type DropDraft,
  type FormContext,
  type InvoiceView,
  type OpsRun,
  type PickupForm,
  type PickupSubject,
} from '@ecobox/firebase';
import {
  Badge,
  Button,
  Callout,
  Card,
  Checkbox,
  Field,
  Input,
  MoneyInput,
  NumberInput,
  ScanInput,
  ScannerView,
  Select,
  scanFeedback,
  type ScanVerdict,
} from '@ecobox/ui';
import type { CurrencyCode } from '@ecobox/domain';
import type { PickupConfig } from '../../hooks/usePickupConfig';
import { loadBarcodeDetector } from '../../scanner/detector';
import { ContactLinks, fmt } from './shared';

export interface StepProps {
  form: PickupForm;
  setForm: (change: (f: PickupForm) => PickupForm) => void;
  ctx: FormContext;
  cfg: PickupConfig;
  currency: CurrencyCode;
  invoice: InvoiceView;
}

/** A scan field with the camera behind it. Feedback is shown by the caller's message. */
function ScanBox({
  label,
  onScan,
  title,
}: {
  label: string;
  onScan: (raw: string) => { ok: boolean; message: string };
  title: string;
}) {
  const [camera, setCamera] = useState(false);
  const [last, setLast] = useState<{ code: string; ok: boolean; message: string } | null>(null);
  const handle = (raw: string): ScanVerdict => {
    const r = onScan(raw);
    setLast({ code: raw.trim().toUpperCase(), ok: r.ok, message: r.message });
    return r.ok ? 'ok' : 'bad';
  };
  return (
    <div className="ops-stack">
      <ScanInput
        label={label}
        autoFocus={false}
        keepFocus={false}
        onScan={(c) => scanFeedback(handle(c))}
        status={last ? (last.ok ? 'ok' : 'error') : 'idle'}
      />
      {last ? (
        <div className="ops-last" data-tone={last.ok ? 'ok' : 'bad'} role="status">
          <span className="ops-code">{last.code}</span> {last.message}
        </div>
      ) : null}
      <Button size="touch" variant="secondary" block onClick={() => setCamera(true)}>
        Scan with the camera
      </Button>
      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title={title}
          count={0}
          countLabel=""
          lastResult={
            last ? { code: last.code, tone: last.ok ? 'ok' : 'bad', message: last.message } : null
          }
          onRead={handle}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}

// ---- 1. the customer ------------------------------------------------------------------------

export function VisitStep({
  subject,
  run,
  form,
  setForm,
  arrived,
  arriving,
  onArrive,
  notify,
  setNotify,
  onFail,
  canAct,
}: {
  subject: PickupSubject;
  run: OpsRun;
  form: PickupForm;
  setForm: StepProps['setForm'];
  arrived: boolean;
  arriving: boolean;
  onArrive: () => void;
  notify: boolean;
  setNotify: (v: boolean) => void;
  onFail: () => void;
  canAct: boolean;
}) {
  const shipper = form.cargo.shipper;
  return (
    <div className="ops-stack">
      <Card title="Customer">
        <div className="ops-stack">
          {subject.contact ? (
            <>
              <div>
                <strong>{subject.contact.name}</strong> · {formatPhone(subject.contact.phoneE164)}
              </div>
              {subject.address ? <div>{formatAddress(subject.address)}</div> : null}
              {subject.address?.landmark ? (
                <div className="ops-muted">Near {subject.address.landmark}</div>
              ) : null}
              <div className="ops-muted">
                {subject.window ? `Window ${subject.window.from} to ${subject.window.to}` : ''}
                {subject.notes ? ` · ${subject.notes}` : ''}
              </div>
              <ContactLinks
                geo={subject.geo}
                address={subject.address}
                phones={[subject.contact.phoneE164]}
                label={subject.contact.name}
              />
            </>
          ) : (
            <>
              <Callout tone="warning">
                This phone does not have the customer’s details, so write the name and phone number.
              </Callout>
              <Field label="Customer’s name" required>
                {(p) => (
                  <Input
                    {...p}
                    size="touch"
                    value={shipper.name}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        cargo: {
                          ...f.cargo,
                          shipper: { ...f.cargo.shipper, mode: 'new', name: e.target.value },
                        },
                      }))
                    }
                  />
                )}
              </Field>
              <Field label="Customer’s phone" required>
                {(p) => (
                  <Input
                    {...p}
                    size="touch"
                    inputMode="tel"
                    value={shipper.phone}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        cargo: {
                          ...f.cargo,
                          shipper: { ...f.cargo.shipper, mode: 'new', phone: e.target.value },
                        },
                      }))
                    }
                  />
                )}
              </Field>
            </>
          )}
        </div>
      </Card>

      <Card title="What to do here" data-tour="stop-arrive">
        <div className="ops-stack">
          {subject.wants.emptyBoxes.length > 0 ? (
            <div>
              Leave empty boxes:{' '}
              {subject.wants.emptyBoxes.map((e) => `${e.qty} × ${e.sizeId}`).join(', ')}
            </div>
          ) : null}
          {subject.wants.cargoPickup ? (
            <div>
              Collect cargo
              {subject.wants.estimatedBoxes > 0
                ? ` (about ${subject.wants.estimatedBoxes} boxes)`
                : ''}
            </div>
          ) : null}
          <Checkbox
            label="I am taking cargo at this stop"
            checked={form.takeCargo}
            onChange={(v) => setForm((f) => ({ ...f, takeCargo: v }))}
          />
          {!arrived ? (
            <>
              <Checkbox
                label="Tell the customer I am arriving soon"
                checked={notify}
                onChange={setNotify}
              />
              <Button
                size="touch"
                variant="secondary"
                block
                loading={arriving}
                disabled={!canAct}
                onClick={onArrive}
              >
                I have arrived
              </Button>
            </>
          ) : (
            <p className="ops-ok" style={{ margin: 0 }}>
              Arrived.
            </p>
          )}
          <Button size="lg" variant="ghost" block onClick={onFail}>
            I could not collect here
          </Button>
          {run.status !== 'active' ? (
            <p role="alert" className="ops-error" style={{ margin: 0 }}>
              The run has not started. Start it from the run page first.
            </p>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

// ---- 2. empty boxes ----------------------------------------------------------------------------

export function DropStep({ form, setForm, cfg, currency }: StepProps) {
  const setDrop = (i: number, patch: Partial<DropDraft>) =>
    setForm((f) => ({ ...f, drops: f.drops.map((d, j) => (j === i ? { ...d, ...patch } : d)) }));
  return (
    <div className="ops-stack">
      {form.drops.length === 0 ? (
        <p className="ops-muted" style={{ margin: 0 }}>
          No empty boxes are left at this stop. Add some if the customer asks for them.
        </p>
      ) : null}
      {form.drops.map((d, i) => (
        <Card key={i} title={`Empty boxes ${i + 1}`}>
          <div className="ops-stack">
            <Field label="Size" required>
              {(p) => (
                <Select
                  {...p}
                  size="touch"
                  value={d.sizeId}
                  onChange={(e) => setDrop(i, { sizeId: e.target.value })}
                >
                  <option value="">Choose…</option>
                  {cfg.sizes.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code} · {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="How many" required>
              {(p) => (
                <NumberInput
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  size="lg"
                  min={1}
                  max={50}
                  value={d.qty}
                  onChange={(qty) => setDrop(i, { qty })}
                />
              )}
            </Field>
            <ScanBox
              label={`Scan the empty boxes left (${d.barcodes.length} of ${d.qty ?? 0})`}
              title={`Empty boxes ${d.sizeId}`}
              onScan={(raw) => {
                const checked = checkDropBarcode(form, raw);
                if (!checked.ok) return { ok: false, message: checked.error };
                if (d.qty && d.barcodes.length >= d.qty) {
                  return { ok: false, message: `You already scanned all ${d.qty}.` };
                }
                setDrop(i, { barcodes: [...d.barcodes, checked.barcode] });
                return { ok: true, message: 'Scanned' };
              }}
            />
            {d.barcodes.length > 0 ? (
              <div className="ops-chips">
                {d.barcodes.map((b) => (
                  <Button
                    key={b}
                    size="sm"
                    variant="secondary"
                    aria-label={`Remove ${b}`}
                    onClick={() => setDrop(i, { barcodes: d.barcodes.filter((x) => x !== b) })}
                  >
                    {b} ✕
                  </Button>
                ))}
              </div>
            ) : null}
            <Button
              size="lg"
              variant="ghost"
              onClick={() => setForm((f) => ({ ...f, drops: f.drops.filter((_, j) => j !== i) }))}
            >
              Remove this size
            </Button>
          </div>
        </Card>
      ))}
      <Button
        size="touch"
        variant="secondary"
        block
        onClick={() =>
          setForm((f) => ({ ...f, drops: [...f.drops, { sizeId: '', qty: 1, barcodes: [] }] }))
        }
      >
        Add empty boxes
      </Button>
      <Field
        label="Cash for empty boxes sold here"
        hint="Only if the customer paid you now for the empty boxes."
      >
        {(p) => (
          <MoneyInput
            id={p.id}
            aria-describedby={p['aria-describedby']}
            size="lg"
            currency={currency}
            value={form.emptyBoxCashMinor}
            onChange={(v) => setForm((f) => ({ ...f, emptyBoxCashMinor: v }))}
          />
        )}
      </Field>
    </div>
  );
}

// ---- 3. cargo boxes -----------------------------------------------------------------------------

export function BoxesStep({ form, setForm, ctx, cfg }: StepProps) {
  const cargo = form.cargo;
  const onlySize = cfg.sizes.length === 1 ? (cfg.sizes[0]?.code ?? '') : '';
  const setBox = (barcode: string, patch: Partial<BoxDraft>) =>
    setForm((f) => ({
      ...f,
      cargo: {
        ...f.cargo,
        boxes: f.cargo.boxes.map((b) => (b.barcode === barcode ? { ...b, ...patch } : b)),
      },
    }));
  const services = allowedServices(ctx.city, ctx.originSectorId, ctx.routes);
  return (
    <div className="ops-stack">
      <Field label="How is it going?" required>
        {(p) => (
          <Select
            {...p}
            size="touch"
            value={cargo.service ?? 'sea'}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                cargo: { ...f.cargo, service: e.target.value === 'air' ? 'air' : 'sea' },
              }))
            }
          >
            <option value="sea">By sea</option>
            <option value="air">By air (weighed)</option>
          </Select>
        )}
      </Field>
      {ctx.city && !services.includes(cargo.service ?? 'sea') ? (
        <Callout tone="warning">
          This destination is not served this way from your country. Prime pieces are taken at the
          counter, not on a pickup.
        </Callout>
      ) : null}
      <Card title={`Scan the boxes · ${cargo.boxes.length}`}>
        <ScanBox
          label="Scan or type a box barcode"
          title="Boxes taken"
          onScan={(raw) => {
            const checked = checkCargoBarcode(form, raw);
            if (!checked.ok) return { ok: false, message: checked.error };
            const last = cargo.boxes[cargo.boxes.length - 1];
            setForm((f) => ({
              ...f,
              cargo: {
                ...f.cargo,
                boxes: [
                  ...f.cargo.boxes,
                  { barcode: checked.barcode, sizeId: last?.sizeId ?? onlySize, sold: false },
                ],
              },
            }));
            return { ok: true, message: 'Scanned' };
          }}
        />
      </Card>
      <ul className="ops-list" aria-label="Boxes taken">
        {cargo.boxes.map((b) => {
          const size = cfg.sizeMap.get(b.sizeId);
          return (
            <li key={b.barcode} className="ops-row">
              <div className="ops-row__top">
                <span className="ops-code">{b.barcode}</span>
                <Button
                  size="lg"
                  variant="ghost"
                  aria-label={`Remove ${b.barcode}`}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      cargo: {
                        ...f.cargo,
                        boxes: f.cargo.boxes.filter((x) => x.barcode !== b.barcode),
                      },
                    }))
                  }
                >
                  Remove
                </Button>
              </div>
              <Field label="Size" required>
                {(p) => (
                  <Select
                    {...p}
                    size="touch"
                    value={b.sizeId}
                    onChange={(e) => setBox(b.barcode, { sizeId: e.target.value })}
                  >
                    <option value="">Choose…</option>
                    {cfg.sizes.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.code} · {s.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field
                label="Weight (kg)"
                required={cargo.service !== 'sea'}
                hint={size?.maxWeightKg ? `At most ${size.maxWeightKg} kg` : undefined}
              >
                {(p) => (
                  <NumberInput
                    id={p.id}
                    aria-describedby={p['aria-describedby']}
                    size="lg"
                    decimals={1}
                    unit="kg"
                    value={b.weightKg}
                    onChange={(weightKg) => setBox(b.barcode, { weightKg })}
                  />
                )}
              </Field>
              {boxNeedsDims(size) ? (
                <div className="ops-two">
                  {(['l', 'w', 'h'] as const).map((k) => (
                    <Field
                      key={k}
                      label={`${k === 'l' ? 'Length' : k === 'w' ? 'Width' : 'Height'} (cm)`}
                    >
                      {(p) => (
                        <NumberInput
                          id={p.id}
                          aria-describedby={p['aria-describedby']}
                          size="lg"
                          value={b.dims?.[k]}
                          onChange={(v) => setBox(b.barcode, { dims: { ...b.dims, [k]: v } })}
                        />
                      )}
                    </Field>
                  ))}
                </div>
              ) : null}
              <Checkbox
                label="The customer bought this empty box now"
                checked={b.sold}
                onChange={(sold) => setBox(b.barcode, { sold })}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---- 4. consignee -------------------------------------------------------------------------------

export function ConsigneeStep({ form, setForm, cfg }: StepProps) {
  const c = form.cargo.consignee;
  const set = (patch: Partial<typeof c>) =>
    setForm((f) => ({
      ...f,
      cargo: { ...f.cargo, consignee: { ...f.cargo.consignee, ...patch } },
    }));
  const results = c.cityId ? [] : searchCities(cfg.cities, c.cityText);
  const chosen = c.cityId ? cfg.cityMap.get(c.cityId) : undefined;
  const cover = chosen ? cfg.coverage.get(chosen.id) : undefined;
  const text = (
    label: string,
    key: 'name' | 'line1' | 'barangay' | 'province' | 'landmark' | 'relationship',
    required = false,
  ) => (
    <Field label={label} required={required}>
      {(p) => (
        <Input
          {...p}
          size="touch"
          value={c[key]}
          onChange={(e) => set({ [key]: e.target.value })}
        />
      )}
    </Field>
  );
  return (
    <div className="ops-stack">
      {text('Consignee’s name', 'name', true)}
      <Field
        label="Consignee’s phone"
        required
        hint="In the Philippines, 0917… or +63917… both work."
      >
        {(p) => (
          <Input
            {...p}
            size="touch"
            inputMode="tel"
            value={c.phones[0] ?? ''}
            onChange={(e) => set({ phones: [e.target.value, ...c.phones.slice(1)] })}
          />
        )}
      </Field>
      {text('Street, house number', 'line1', true)}
      {text('Barangay', 'barangay')}
      <Field label="City or town" required>
        {(p) => (
          <Input
            {...p}
            size="touch"
            value={c.cityText}
            placeholder="Start typing the city"
            onChange={(e) => set({ cityText: e.target.value, cityId: '' })}
          />
        )}
      </Field>
      {results.length > 0 ? (
        <ul className="ops-list" aria-label="Cities">
          {results.map((city) => (
            <li key={city.id}>
              <Button
                size="touch"
                variant="secondary"
                block
                onClick={() => set({ cityId: city.id, cityText: city.displayName })}
              >
                {city.displayName}, {city.province}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {chosen ? (
        <p className="ops-ok" style={{ margin: 0 }}>
          {chosen.displayName}, {chosen.province}.{' '}
          {cover?.mode === 'own'
            ? 'EcoBox delivers here itself.'
            : cover
              ? 'A partner delivers here.'
              : ''}
        </p>
      ) : null}
      {text('Province', 'province')}
      {text('Landmark', 'landmark')}
      {text('Relationship to the customer', 'relationship')}
    </div>
  );
}

// ---- 5. contents --------------------------------------------------------------------------------

export function ContentsStep({ form, setForm, currency }: StepProps) {
  const cargo = form.cargo;
  const setRow = (i: number, patch: Partial<(typeof cargo.packing)[number]>) =>
    setForm((f) => ({
      ...f,
      cargo: {
        ...f.cargo,
        packing: f.cargo.packing.map((p, j) => (j === i ? { ...p, ...patch } : p)),
      },
    }));
  return (
    <div className="ops-stack">
      <p className="ops-muted" style={{ margin: 0 }}>
        What is in the boxes. Customs may ask for this.
      </p>
      {cargo.packing.map((row, i) => (
        <Card key={i} title={`Item ${i + 1}`}>
          <div className="ops-stack">
            <Field label="What is it" required>
              {(p) => (
                <Input
                  {...p}
                  size="touch"
                  value={row.description}
                  onChange={(e) => setRow(i, { description: e.target.value })}
                />
              )}
            </Field>
            <Field label="How many" required>
              {(p) => (
                <NumberInput
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  size="lg"
                  min={1}
                  value={row.qty}
                  onChange={(qty) => setRow(i, { qty })}
                />
              )}
            </Field>
            <Field label="Value of all of them">
              {(p) => (
                <MoneyInput
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  size="lg"
                  currency={currency}
                  value={row.valueMinor}
                  onChange={(valueMinor) => setRow(i, { valueMinor })}
                />
              )}
            </Field>
            {cargo.packing.length > 1 ? (
              <Button
                size="lg"
                variant="ghost"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    cargo: { ...f.cargo, packing: f.cargo.packing.filter((_, j) => j !== i) },
                  }))
                }
              >
                Remove this item
              </Button>
            ) : null}
          </div>
        </Card>
      ))}
      <Button
        size="touch"
        variant="secondary"
        block
        onClick={() =>
          setForm((f) => ({
            ...f,
            cargo: {
              ...f.cargo,
              packing: [
                ...f.cargo.packing,
                { description: '', qty: undefined, valueMinor: undefined },
              ],
            },
          }))
        }
      >
        Add another item
      </Button>
      <Field label="Declared value" required hint="0 if the customer declares nothing.">
        {(p) => (
          <MoneyInput
            id={p.id}
            aria-describedby={p['aria-describedby']}
            size="lg"
            currency={currency}
            value={cargo.declaredMinor}
            onChange={(declaredMinor) =>
              setForm((f) => ({ ...f, cargo: { ...f.cargo, declaredMinor } }))
            }
          />
        )}
      </Field>
      <Checkbox
        label="I told the customer which items are prohibited"
        checked={cargo.prohibitedAck}
        onChange={(prohibitedAck) =>
          setForm((f) => ({ ...f, cargo: { ...f.cargo, prohibitedAck } }))
        }
      />
    </div>
  );
}

// ---- 6. the price and the payment -----------------------------------------------------------------

export function PayStep({
  form,
  setForm,
  invoice,
  currency,
  addOns,
}: StepProps & { addOns: readonly { code: string; label: string }[] }) {
  const paid = paymentsTotal(form.cargo.payments);
  const cash = form.cargo.payments[0]?.amountMinor;
  return (
    <div className="ops-stack">
      {addOns.length > 0 ? (
        <Card title="Extras">
          <div className="ops-stack">
            {addOns.map((a) => (
              <Checkbox
                key={a.code}
                label={a.label}
                checked={form.cargo.addOnCodes.includes(a.code)}
                onChange={(on) =>
                  setForm((f) => ({
                    ...f,
                    cargo: {
                      ...f.cargo,
                      addOnCodes: on
                        ? [...f.cargo.addOnCodes, a.code]
                        : f.cargo.addOnCodes.filter((c) => c !== a.code),
                    },
                  }))
                }
              />
            ))}
          </div>
        </Card>
      ) : null}
      <Card title="Price">
        {invoice.ok ? (
          <div className="ops-stack">
            <ul className="ops-list" aria-label="Price lines">
              {invoice.lines.map((l, i) => (
                <li key={i} className="ops-row">
                  <div className="ops-row__top">
                    <span>{l.label}</span>
                    <strong>{fmt(l.amountMinor, invoice.currency)}</strong>
                  </div>
                  {l.basis ? <div className="ops-muted">{l.basis}</div> : null}
                </li>
              ))}
            </ul>
            <div className="ops-row__top">
              <strong>Total (VAT included)</strong>
              <span className="ops-money" aria-label="Total">
                {fmt(invoice.grossMinor, invoice.currency)}
              </span>
            </div>
            <div className="ops-muted">
              VAT {invoice.vatRatePercent}%: {fmt(invoice.vatMinor, invoice.currency)}
            </div>
          </div>
        ) : (
          <Callout tone="warning">{invoice.reason}</Callout>
        )}
      </Card>
      {invoice.ok ? (
        <Card title="Payment">
          <div className="ops-stack">
            <Field label="Cash you took now" hint="Leave it empty if the customer will pay later.">
              {(p) => (
                <MoneyInput
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  size="lg"
                  currency={currency}
                  value={cash}
                  onChange={(v) => setForm((f) => withCashPayment(f, v))}
                />
              )}
            </Field>
            <div className="ops-row__actions">
              <Button
                size="lg"
                variant="secondary"
                onClick={() => setForm((f) => withCashPayment(f, invoice.grossMinor))}
              >
                Paid in full
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => setForm((f) => withCashPayment(f, undefined))}
              >
                Leave unpaid
              </Button>
            </div>
            {paid === 0 ? (
              <Badge tone="warning">Unpaid · the customer pays later</Badge>
            ) : paid < invoice.grossMinor ? (
              <Badge tone="warning">
                Part paid · {fmt(invoice.grossMinor - paid, invoice.currency)} to pay
              </Badge>
            ) : (
              <Badge tone="success">Paid in full</Badge>
            )}
          </div>
        </Card>
      ) : null}
      {form.cargo.boxes.length > 0 ? (
        <p className="ops-muted" style={{ margin: 0 }}>
          {form.cargo.boxes.length} box{form.cargo.boxes.length === 1 ? '' : 'es'} ·{' '}
          {form.cargo.consignee.name} · {form.cargo.consignee.cityText}
        </p>
      ) : null}
    </div>
  );
}
